// The authoritative "applied from" base version of each PATCH entry in an
// executed bundle — the version the patch was conditioned on, read off the
// response entries that `SeenMedplumClient.executeBatch` stamps before
// returning. The completed queued-edits diff reads these exact versions as its
// baseline so the recorded before/after stays accurate after the resources are
// edited.
//
// Why the response, not the request bundle: executeBatch resolves the base
// version internally (fhirpath→json conversion, plus an in-place rebase onto a
// newer version on a `test /meta/versionId` conflict). The caller cannot know
// that version up front, so executeBatch reports it on each PATCH response
// entry via the `batch-entry-base-version` extension. Reading the mutated
// request bundle instead would couple callers to executeBatch internals.

import type { Bundle, Extension, Reference, Resource } from '@medplum/fhirtypes'
import type { Operation } from 'rfc6902'
import { BATCH_ENTRY_BASE_VERSION_EXTENSION } from '#src/codeSystems.ts'
import { base64Decode } from '#src/encoding.ts'

// The versionId a Binary json-patch payload is conditioned on — the value of
// its leading `test /meta/versionId` op. By the time a bundle has been
// executed, every fhirpath-patch entry has been converted to this Binary
// json-patch form, so this is the single shape to read. Returns undefined for
// non-Binary payloads or patches without a version precondition.
export function patchEntryBaseVersion(
  payload: Resource | undefined
): string | undefined {
  if (
    payload?.resourceType !== 'Binary' ||
    !payload.data ||
    payload.contentType !== 'application/json-patch+json'
  ) {
    return undefined
  }
  let ops: Operation[]
  try {
    ops = JSON.parse(base64Decode(payload.data)) as Operation[]
  } catch {
    return undefined
  }
  const testOp = ops.find(
    (op) => op.op === 'test' && op.path === '/meta/versionId'
  )
  return testOp && 'value' in testOp && typeof testOp.value === 'string'
    ? testOp.value
    : undefined
}

// The versioned reference (`ResourceType/id/_history/{versionId}`) executeBatch
// stamped on a single response entry, if any.
function baseVersionRef(extension?: Extension[]): string | undefined {
  return extension?.find((e) => e.url === BATCH_ENTRY_BASE_VERSION_EXTENSION)
    ?.valueString
}

// The versioned references (`ResourceType/id/_history/{versionId}`) each PATCH
// entry was applied from, read from the extension executeBatch stamped on the
// response entries. Entries with no stamp (PUT/DELETE, unconditional PATCH,
// conditional url) contribute nothing — their baseline is not version-pinned,
// and the diff degrades gracefully without one. A transaction-response entry
// carries no request url, so the full reference is read straight off the stamp.
export function appliedFromVersionRefs(executedBundle: Bundle): Reference[] {
  const refs: Reference[] = []
  for (const entry of executedBundle.entry ?? []) {
    const ref = baseVersionRef(entry.response?.extension)
    if (ref) {
      refs.push({ reference: ref })
    }
  }
  return refs
}
